import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

export interface PageContent {
  _id?: string;
  pageSlug: string;
  pageTitle: string;
  sections: PageSection[];
  seo: SEOData;
  status: 'draft' | 'published' | 'archived';
  publishedAt?: Date;
  version: number;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  updatedBy?: string;
}

export interface PageSection {
  id: string;
  type: string;
  title?: string;
  subtitle?: string;
  description?: string;
  content?: string;
  items?: PageSectionItem[];
  image?: string;
  backgroundImage?: string;
  cta?: CTAButton[];
  order: number;
  config?: any;
}

export interface PageSectionItem {
  id: string;
  title: string;
  description: string;
  icon?: string;
  image?: string;
  link?: string;
  order: number;
  features?: string[];
}

export interface CTAButton {
  text: string;
  link: string;
  style?: string;
}

export interface SEOData {
  metaTitle: string;
  metaDescription: string;
  keywords: string[];
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
}

export interface ContactInfo {
  _id?: string;
  type: 'phone' | 'email' | 'address' | 'hours' | 'social';
  label: string;
  value: string;
  icon?: string;
  order: number;
  active: boolean;
  metadata?: {
    platform?: string;
    displayFormat?: string;
    mapUrl?: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface Settings {
  _id?: string;
  key: string;
  value: any;
  description?: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  category?: string;
  isPublic: boolean;
  updatedAt: Date;
  updatedBy?: string;
}

class PageService {
  // Page Content Methods
  async getAllPages(includeUnpublished = false): Promise<PageContent[]> {
    try {
      const params = includeUnpublished ? { includeUnpublished: 'true' } : {};
      const response = await axios.get(`${API_BASE_URL}/api/pages`, { params });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching pages:', error);
      throw error;
    }
  }

  async getPageContent(slug: string): Promise<PageContent> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/pages/${slug}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching page ${slug}:`, error);
      throw error;
    }
  }

  async createPageContent(data: Partial<PageContent>): Promise<PageContent> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/pages`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error creating page:', error);
      throw error;
    }
  }

  async updatePageContent(id: string, data: Partial<PageContent>): Promise<PageContent> {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/pages/${id}`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error updating page:', error);
      throw error;
    }
  }

  async deletePageContent(id: string): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/api/pages/${id}`);
    } catch (error) {
      console.error('Error deleting page:', error);
      throw error;
    }
  }

  async publishPage(id: string): Promise<PageContent> {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/pages/${id}/publish`);
      return response.data.data;
    } catch (error) {
      console.error('Error publishing page:', error);
      throw error;
    }
  }

  async unpublishPage(id: string): Promise<PageContent> {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/pages/${id}/unpublish`);
      return response.data.data;
    } catch (error) {
      console.error('Error unpublishing page:', error);
      throw error;
    }
  }

  async getPagesByStatus(status: string): Promise<PageContent[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/pages/status/${status}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching pages with status ${status}:`, error);
      throw error;
    }
  }

  async searchPages(query: string): Promise<PageContent[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/pages/search/${encodeURIComponent(query)}`);
      return response.data.data;
    } catch (error) {
      console.error('Error searching pages:', error);
      throw error;
    }
  }

  // Contact Info Methods
  async getContactInfo(): Promise<ContactInfo[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/contact-info`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching contact info:', error);
      throw error;
    }
  }

  async createContactInfo(data: Partial<ContactInfo>): Promise<ContactInfo> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/contact-info`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error creating contact info:', error);
      throw error;
    }
  }

  async updateContactInfo(id: string, data: Partial<ContactInfo>): Promise<ContactInfo> {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/contact-info/${id}`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error updating contact info:', error);
      throw error;
    }
  }

  async deleteContactInfo(id: string): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/api/contact-info/${id}`);
    } catch (error) {
      console.error('Error deleting contact info:', error);
      throw error;
    }
  }

  // Settings Methods
  async getAllSettings(includePrivate = false): Promise<Settings[]> {
    try {
      const params = includePrivate ? { includePrivate: 'true' } : {};
      const response = await axios.get(`${API_BASE_URL}/api/settings`, { params });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  }

  async getSetting(key: string): Promise<Settings> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/settings/${key}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching setting ${key}:`, error);
      throw error;
    }
  }

  async createSetting(data: Partial<Settings>): Promise<Settings> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/settings`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error creating setting:', error);
      throw error;
    }
  }

  async updateSetting(key: string, value: any, updatedBy?: string): Promise<Settings> {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/settings/${key}`, {
        value,
        updatedBy
      });
      return response.data.data;
    } catch (error) {
      console.error('Error updating setting:', error);
      throw error;
    }
  }

  async deleteSetting(key: string): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/api/settings/${key}`);
    } catch (error) {
      console.error('Error deleting setting:', error);
      throw error;
    }
  }
}

export const pageService = new PageService();
export default pageService;